import { useState, useRef, useEffect } from 'react';
import { FigurePanel, useFigureCompositionStore } from '@/store/figure-composition';

interface PanelLabelProps {
  panel: FigurePanel;
  editable?: boolean;
}

export function PanelLabel({ panel, editable = true }: PanelLabelProps) {
  const updatePanel = useFigureCompositionStore((state) => state.updatePanel);
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(panel.label);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  useEffect(() => {
    setEditValue(panel.label);
  }, [panel.label]);

  const handleClick = (e: React.MouseEvent) => {
    if (editable) {
      e.stopPropagation();
      setIsEditing(true);
    }
  };

  const handleBlur = () => {
    if (editValue.trim() && editValue !== panel.label) {
      updatePanel(panel.id, { label: editValue.trim() });
    } else {
      setEditValue(panel.label);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleBlur();
    } else if (e.key === 'Escape') {
      setEditValue(panel.label);
      setIsEditing(false);
    }
  };

  // Don't render if label is empty or undefined
  if (!panel.label || panel.label.trim() === '') {
    return null;
  }

  const labelStyle = panel.labelStyle ?? {};
  const position = labelStyle.position ?? 'top-left';
  const fontSize = labelStyle.fontSize ?? 10;
  const fontWeight = labelStyle.fontWeight ?? 'bold';
  const fontStyle = labelStyle.fontStyle ?? 'normal';

  // Calculate position classes
  const positionClasses = {
    'top-left': 'top-2 left-2',
    'top-right': 'top-2 right-2',
    'bottom-left': 'bottom-2 left-2',
    'bottom-right': 'bottom-2 right-2',
  };

  const style: React.CSSProperties = {
    fontSize: `${fontSize}pt`,
    fontWeight,
    fontStyle,
  };

  if (isEditing) {
    return (
      <div className={`absolute ${positionClasses[position]} z-20`}>
        <input
          ref={inputRef}
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          className="bg-white px-1.5 py-0.5 rounded text-xs text-gray-700 border border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          onClick={(e) => e.stopPropagation()}
          style={{ minWidth: '40px', ...style }}
        />
      </div>
    );
  }

  return (
    <div
      data-panel-label
      className={`absolute ${positionClasses[position]} z-10 bg-white/90 px-1.5 py-0.5 rounded text-gray-700 ${editable ? 'cursor-text pointer-events-auto hover:bg-white' : 'pointer-events-none'}`}
      onClick={handleClick}
      title={editable ? 'Click to edit label' : undefined}
      style={style}
    >
      {panel.label}
    </div>
  );
}

